import { CurrencyOption } from '@copmer/frc-api-types';
import { Control, FieldArrayPath, FieldPath, FieldValues, useFieldArray } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form';

import { makeCurrencySelectSuffix } from './currency-select-suffix';
import { numericOnChange } from './helpers';
import Icon from './Icon';
import { CalculatorInput } from './Inputs';

interface MiscCostsFieldProps<T extends FieldValues> {
    control: Control<T>;
    name: FieldArrayPath<T>;
    disabled?: boolean;

    extraChildren?: React.ReactNode;
}

export function MiscCostsField<T extends FieldValues>({
    control,
    name,
    disabled,
    extraChildren,
}: MiscCostsFieldProps<T>) {
    const { fields, append, remove } = useFieldArray({ control, name });

    const labelPath = (index: number) => `${name}.${index}.label` as FieldPath<T>;
    const amountPath = (index: number) => `${name}.${index}.amount` as FieldPath<T>;
    const currencyPath = (index: number) => `${name}.${index}.currency` as FieldPath<T>;

    const hasRows = fields.length > 0;

    return (
        <div className={`flex flex-col gap-2 w-full ${hasRows ? 'mt-2' : ''}`}>
            {fields.map((field, index) => (
                <div key={field.id} className="flex gap-2 items-start">
                    <div className="flex-1">
                        <FormField
                            control={control}
                            name={labelPath(index)}
                            render={({ field: labelField, fieldState: { error } }) => (
                                <CalculatorInput
                                    type="text"
                                    label={index === 0 ? 'Misc cost label' : ''}
                                    plainLabel={index !== 0}
                                    isRequired
                                    placeholder="Label"
                                    inputProps={{
                                        ...labelField,
                                        value: labelField.value ?? '',
                                    }}
                                    onChange={labelField.onChange}
                                    error={error?.message}
                                    disabled={disabled}
                                    tabIndex={0}
                                />
                            )}
                        />
                    </div>

                    <div className="flex-1">
                        <FormField
                            control={control}
                            name={amountPath(index)}
                            render={({ field: amountField, fieldState: { error } }) => (
                                <CalculatorInput
                                    type="text"
                                    label={index === 0 ? 'Amount' : ''}
                                    plainLabel={index !== 0}
                                    isRequired
                                    placeholder="Amount"
                                    suffix={makeCurrencySelectSuffix(control, currencyPath(index))}
                                    inputProps={{
                                        ...amountField,
                                        inputMode: 'decimal',
                                        value: amountField.value ?? '',
                                    }}
                                    onChange={numericOnChange(amountField.onChange)}
                                    error={error?.message}
                                    disabled={disabled}
                                    tabIndex={0}
                                />
                            )}
                        />
                    </div>

                    <Button
                        type="button"
                        variant="icon"
                        size="snug"
                        className={
                            index === 0
                                ? 'mt-5 hover:border-transparent text-frGrey-400'
                                : 'hover:border-transparent text-frGrey-400'
                        }
                        onClick={() => remove(index)}
                        disabled={disabled}
                        tabIndex={0}
                        aria-label="Remove cost"
                    >
                        <Icon icon="SquareRoundedMinus" className="w-6 h-6" />
                    </Button>
                </div>
            ))}

            <div className="flex gap-4 items-center w-full">
                <Button
                    type="button"
                    variant="darkText"
                    className="py-1 text-primary-200 hover:text-primary-100 px-0"
                    onClick={() =>
                        append({
                            label: '',
                            amount: '',
                            currency: CurrencyOption.USD,
                        } as any)
                    }
                    disabled={disabled}
                    tabIndex={0}
                >
                    <Icon icon="Plus" className="w-4 h-4 mr-1" />
                    Add misc cost
                </Button>

                {extraChildren}
            </div>
        </div>
    );
}
